import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useClient } from 'jsonapi-react';
import { v4 as uuid } from 'uuid';
import jsQR from 'jsqr';
import { toast } from 'react-hot-toast';
import { t } from 'i18next';
import api from '@/services/api';
import { uploadFileFromPresignedUrl } from '@/utils/aws-sdk';
import { DashboardContent } from '@/layouts/dashboard';

// Upload em lote de cartoes-resposta escaneados (Opcao A, fase 3). Le o QR de
// cada imagem (jsQR), resolve o token -> aluno (backend), e envia reusando o
// mesmo fluxo do StudentCard (presign S3 -> upload -> cria answer_sheet).
// QR ilegivel / erro nao trava o lote: cada arquivo tem seu resultado.
export function BulkAnswerSheetUpload() {
  const { id } = useParams();
  const client = useClient();
  const [results, setResults] = useState([]); // { fileName, status, message, studentName }
  const [processing, setProcessing] = useState(false);

  // decodifica o QR de um arquivo de imagem via canvas + jsQR
  const decodeQrFromImage = file =>
    new Promise(resolve => {
      const img = new Image();
      const url = URL.createObjectURL(file);
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = img.naturalWidth;
          canvas.height = img.naturalHeight;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0);
          const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
          const code = jsQR(imageData.data, imageData.width, imageData.height);
          resolve(code?.data || null);
        } catch (e) {
          resolve(null);
        } finally {
          URL.revokeObjectURL(url);
        }
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        resolve(null);
      };
      img.src = url;
    });

  const resolveStudent = async token => {
    // { user-id, question-book-id, name } ou lanca em token invalido (422)
    const res = await api.get(`answer_sheets/resolve?token=${encodeURIComponent(token)}`);
    return res?.data;
  };

  const uploadForStudent = async (file, userId) => {
    const ext = file.name.includes('.') ? '.' + file.name.split('.').pop() : '';
    const renamed = new File([file], uuid().replaceAll('-', '') + ext, { type: file.type });

    const { data, error } = await client.mutate(`question_books/${id}/answer_sheets/presign_s3_url`, {
      file_key: `uploads/answer_sheet/${renamed.name}`,
      content_type: renamed.type
    });
    if (error) throw new Error('presign');

    const uploadedUrl = await uploadFileFromPresignedUrl(data['presigned-url'], renamed);
    if (!uploadedUrl) throw new Error('upload');

    const { error: createError } = await client.mutate(`question_books/${id}/answer_sheets`, {
      url: uploadedUrl,
      'question-books-user-attributes': { 'user-id': userId, 'question-book-id': id }
    });
    if (createError) throw new Error('create');
  };

  const handleFiles = async e => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    setProcessing(true);
    setResults(files.map(f => ({ fileName: f.name, status: 'pending', message: t('common.processing', 'Processando...') })));

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const update = patch => setResults(prev => prev.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
      try {
        const isImage = (file.type || '').startsWith('image/');
        if (!isImage) {
          update({ status: 'skipped', message: 'PDF: leia o QR não é suportado no lote — envie por aluno' });
          continue;
        }
        const qr = await decodeQrFromImage(file);
        if (!qr) {
          update({ status: 'error', message: 'QR ilegível — reenvie com mais nitidez ou por aluno' });
          continue;
        }
        const student = await resolveStudent(qr);
        update({ studentName: student?.name, message: `Enviando para ${student?.name}...` });
        await uploadForStudent(file, student['user-id']);
        update({ status: 'ok', message: `Enviado ✓ (${student?.name})` });
      } catch (err) {
        const msg = err?.response?.status === 422 ? 'QR inválido/adulterado' : 'Falha no envio';
        update({ status: 'error', message: msg });
      }
    }

    setProcessing(false);
    toast.success(t('exams.bulkDone', 'Lote processado'));
  };

  const badge = status =>
    ({ ok: 'success', error: 'danger', skipped: 'warning', pending: 'primary' }[status] || 'primary');

  return (
    <DashboardContent variant="plain">
      <h1 className="u-mb-2">{t('exams.bulkUploadTitle', 'Enviar cartões-resposta em lote')}</h1>
      <p className="u-mb-4">
        {t('exams.bulkUploadHelp', 'Selecione as imagens dos cartões escaneados. O QR de cada folha identifica o aluno automaticamente.')}
      </p>

      <label className="btn btn--wide btn--primary">
        {t('button.selectFiles', 'Selecionar imagens')}
        <input
          type="file"
          accept="image/*"
          multiple
          hidden
          disabled={processing}
          onChange={handleFiles}
        />
      </label>

      {results.length > 0 && (
        <ul className="u-mt-4" style={{ listStyle: 'none', padding: 0 }}>
          {results.map((r, i) => (
            <li key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderBottom: '1px solid #eee' }}>
              <span className={`badge badge--tiny badge--${badge(r.status)}`}>{r.status}</span>
              <strong>{r.fileName}</strong>
              <span style={{ color: '#666' }}>— {r.message}</span>
            </li>
          ))}
        </ul>
      )}
    </DashboardContent>
  );
}
